import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authApi } from '../services/api';
import { User } from '../types';

interface LoginResult {
  requiresMFA?: boolean;
  mfaToken?: string;
  requiresMFASetup?: boolean;
  setupToken?: string;
  success?: boolean;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<LoginResult | void>;
  logout: () => void;
  setUser: (user: User | null) => void;
  setToken: (token: string | null) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const storedToken = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');

    if (storedToken && storedUser) {
      setToken(storedToken);
      setUser(JSON.parse(storedUser));
      authApi.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
    }

    setLoading(false);
  }, []);

  const login = async (email: string, password: string) => {
    const response = await authApi.post('/auth/login', { email, password });
    
    // Check if MFA is required
    if (response.data.requiresMFA) {
      // Return MFA token for verification
      return {
        requiresMFA: true,
        mfaToken: response.data.mfaToken,
      };
    }

    // Check if MFA setup is required (first login)
    if (response.data.requiresMFASetup) {
      // Return setup token for MFA setup
      return {
        requiresMFASetup: true,
        setupToken: response.data.setupToken,
      };
    }

    // Check if MFA is optional (MFA disabled but secret exists)
    if (response.data.mfaOptional) {
      // Return optional MFA token
      return {
        mfaOptional: true,
        mfaToken: response.data.mfaToken,
      };
    }

    // Normal login - no MFA required
    const { user: userData, token: newToken } = response.data;
    setUser(userData);
    setToken(newToken);
    localStorage.setItem('token', newToken);
    localStorage.setItem('user', JSON.stringify(userData));
    authApi.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    
    return { success: true };
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    delete authApi.defaults.headers.common['Authorization'];
    authApi.post('/auth/logout').catch(() => {});
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, logout, setUser, setToken }}>
      {children}
    </AuthContext.Provider>
  );
};

